import { rankingItems } from "@/mocks/home";
import SectionHeading from "./SectionHeading";

const trendIcon: Record<string, string> = {
  up: "ri-arrow-up-line",
  down: "ri-arrow-down-line",
  flat: "ri-subtract-line",
};
const trendColor: Record<string, string> = {
  up: "text-primary-600",
  down: "text-secondary-600",
  flat: "text-foreground-400",
};

function rankBadge(rank: number) {
  if (rank === 1) return "bg-primary-500 text-background-50";
  if (rank === 2) return "bg-accent-500 text-foreground-950";
  if (rank === 3) return "bg-foreground-800 text-background-50";
  return "bg-background-200 text-foreground-700";
}

export default function EndorsementRanking() {
  return (
    <section id="endorse-rank" className="w-full border-b border-background-200 bg-background-100 py-10 md:py-14">
      <div className="mx-auto max-w-[1400px] px-4 md:px-6">
        <SectionHeading
          no="02"
          title="代言风险指数榜"
          desc="以讨论热度、事件性质与合作关联度合成的综合排序。热度越高不代表结论越重，它更多是一份「谁在牌桌上」的提示。"
          actionLabel="查看完整榜单"
          actionHref="#endorse-rank"
        />

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
          {/* ranking list */}
          <div className="lg:col-span-8">
            <div className="overflow-hidden rounded-lg border border-background-200 bg-background-50">
              <div className="hidden items-center gap-4 border-b border-background-200 bg-background-100/70 px-5 py-3 text-[11px] font-semibold text-foreground-500 md:flex">
                <span className="w-8">排名</span>
                <span className="flex-1">主体 / 领域</span>
                <span className="w-40">热度</span>
                <span className="w-20 text-right">风险分</span>
              </div>

              {rankingItems.map((item) => (
                <article
                  key={item.rank}
                  className="flex flex-col gap-3 border-b border-background-200 px-4 py-4 transition-colors last:border-b-0 hover:bg-background-100/60 md:flex-row md:items-center md:gap-4 md:px-5"
                >
                  <div className="flex items-center gap-3 md:contents">
                    <span
                      className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-md font-heading text-sm font-black ${rankBadge(
                        item.rank,
                      )}`}
                    >
                      {item.rank}
                    </span>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <h4 className="text-sm font-bold text-foreground-950 md:text-base">{item.name}</h4>
                        <span className="rounded-sm bg-background-200 px-1.5 py-0.5 text-[10px] text-foreground-600">
                          {item.category}
                        </span>
                        {item.tags.map((t) => (
                          <span
                            key={t}
                            className="rounded-sm bg-primary-50 px-1.5 py-0.5 text-[10px] font-medium text-primary-700"
                          >
                            {t}
                          </span>
                        ))}
                      </div>
                      <p className="mt-1 hidden text-xs leading-relaxed text-foreground-500 lg:block">{item.note}</p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 md:block md:w-40">
                    <div className="h-2 flex-1 overflow-hidden rounded-full bg-background-200 md:w-full">
                      <div
                        className="h-full rounded-full bg-accent-500"
                        style={{ width: `${item.heat}%` }}
                      ></div>
                    </div>
                    <span className="text-xs font-semibold text-foreground-600 md:mt-1 md:block">
                      {item.heat}
                    </span>
                  </div>

                  <div className="flex items-center justify-between md:w-20 md:justify-end md:gap-2">
                    <span className="font-heading text-lg font-black text-foreground-950">{item.riskScore}</span>
                    <span className={`flex items-center gap-0.5 text-xs ${trendColor[item.trend]}`}>
                      <i className={`${trendIcon[item.trend]} w-3.5 h-3.5 flex items-center justify-center`}></i>
                      {item.trendValue}
                    </span>
                  </div>
                </article>
              ))}
            </div>
          </div>

          {/* side panel */}
          <aside className="lg:col-span-4">
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h4 className="flex items-center gap-2 font-heading text-base font-bold text-foreground-950">
                <i className="ri-bar-chart-2-line w-5 h-5 flex items-center justify-center text-primary-500"></i>
                榜单怎么读
              </h4>
              <ul className="mt-4 space-y-3 text-xs leading-relaxed text-foreground-600">
                <li className="flex gap-2">
                  <i className="ri-fire-line w-4 h-4 flex items-center justify-center text-accent-600"></i>
                  <span><strong className="text-foreground-800">热度</strong>：近 72 小时公开讨论的相对强度。</span>
                </li>
                <li className="flex gap-2">
                  <i className="ri-alert-line w-4 h-4 flex items-center justify-center text-primary-500"></i>
                  <span><strong className="text-foreground-800">风险分</strong>：结合事件性质与合作关联度的综合判断。</span>
                </li>
                <li className="flex gap-2">
                  <i className="ri-line-chart-line w-4 h-4 flex items-center justify-center text-secondary-600"></i>
                  <span><strong className="text-foreground-800">趋势</strong>：与前一周期对比的变化方向。</span>
                </li>
              </ul>

              <div className="mt-5 grid grid-cols-3 gap-2 border-t border-background-200 pt-5 text-center">
                {[
                  { k: "在监主体", v: "286" },
                  { k: "周新增", v: "41" },
                  { k: "已复核", v: "93%" },
                ].map((s) => (
                  <div key={s.k}>
                    <p className="font-heading text-xl font-black text-foreground-950">{s.v}</p>
                    <p className="mt-0.5 text-[11px] text-foreground-500">{s.k}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="mt-4 rounded-lg bg-primary-500 p-5 text-background-50">
              <i className="ri-shield-check-line w-6 h-6 flex items-center justify-center text-2xl text-accent-400"></i>
              <h4 className="mt-3 font-heading text-base font-bold">需要按合作逐项排查？</h4>
              <p className="mt-1.5 text-xs leading-relaxed text-background-100">
                订阅后可在官宣、续约与物料上线前，收到与你合作清单相关的风险提醒。
              </p>
              <a
                href="#subscribe"
                className="mt-4 flex items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-background-50 px-4 py-2.5 text-xs font-semibold text-primary-700 transition-colors hover:bg-background-100"
              >
                订阅风险预警
                <i className="ri-arrow-right-line w-3.5 h-3.5 flex items-center justify-center"></i>
              </a>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}